import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Theme from '../../Theme.config';

/**
 * Session Expiry Dialog Component
 * Shows a dialog when access token expires, asking user to continue or logout
 */
const SessionExpiryDialog = ({ open, onContinue, onLogout }) => {
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="session-expiry-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 400,
        },
      }}
    >
      <DialogTitle id="session-expiry-dialog-title" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6" component="span">
            Session Expiring
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          Your session is about to expire. Would you like to continue your session?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={onLogout}
          sx={{ minWidth: 100 }}
        >
          No, Logout
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onContinue}
          sx={{ minWidth: 100 }}
        >
          Yes, Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Container element for the dialog
let dialogContainer = null;
let dialogRoot = null;

/**
 * Show the session expiry dialog
 * @returns {Promise<boolean>} - Returns true if user clicks "Continue", false if "Logout"
 */
export const showSessionExpiryDialog = () => {
  return new Promise((resolve) => {
    // Create container if it doesn't exist
    if (!dialogContainer) {
      dialogContainer = document.createElement('div');
      dialogContainer.id = 'session-expiry-dialog-container';
      document.body.appendChild(dialogContainer);
      dialogRoot = ReactDOM.createRoot(dialogContainer);
    }

    const handleContinue = () => {
      dialogRoot.render(<SessionExpiryDialog open={false} />);
      resolve(true);
    };

    const handleLogout = () => {
      dialogRoot.render(<SessionExpiryDialog open={false} />);
      // Redirect to login page
      window.location.href = '/login';
      resolve(false);
    };

    dialogRoot.render(
      <ThemeProvider theme={Theme}>
        <CssBaseline />
        <SessionExpiryDialog
          open={true}
          onContinue={handleContinue}
          onLogout={handleLogout}
        />
      </ThemeProvider>
    );
  });
};

export default SessionExpiryDialog;
